import { useState, useEffect, useCallback } from "react";
import { supabase, Student, SchoolClass, School, logActivity } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { UserPlus, CheckCircle2, Users, School as SchoolIcon } from "lucide-react";
import ExcelBulkUpload from "@/components/ExcelBulkUpload";

// Standard Malawian primary-school structure: ECD (one class) plus
// Grade 1 through Grade 8, each split into sections A-D. These are
// auto-provisioned into the `classes` table for every school + shift +
// academic year combination the first time a primary-level student is
// registered there, so Student Registration and Attendance always
// reference identical class names on any device.
const GRADE_SECTIONS = ["A", "B", "C", "D"];
const GRADES = ["1", "2", "3", "4", "5", "6", "7", "8"];

function buildStandardPrimaryClasses(): { name: string; grade: string; section: string | null }[] {
  const list: { name: string; grade: string; section: string | null }[] = [
    { name: "ECD", grade: "ECD", section: null },
  ];
  GRADES.forEach((g) => {
    GRADE_SECTIONS.forEach((s) => {
      list.push({ name: `Grade ${g}${s}`, grade: g, section: s });
    });
  });
  return list;
}

const STANDARD_PRIMARY_CLASSES = buildStandardPrimaryClasses();

const currentAcademicYear = new Date().getFullYear();
const ACADEMIC_YEAR_OPTIONS = [currentAcademicYear - 1, currentAcademicYear, currentAcademicYear + 1];

export default function StudentRegistration() {
  const { user } = useAuth();
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState<"female" | "male">("female");
  const [level, setLevel] = useState<"preschool" | "primary" | "secondary">("preschool");
  const [guardianName, setGuardianName] = useState("");
  const [guardianPhone, setGuardianPhone] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [classId, setClassId] = useState("");
  const [shift, setShift] = useState<"morning" | "afternoon">("morning");
  const [academicYear, setAcademicYear] = useState(currentAcademicYear);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [refCode, setRefCode] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [provisioningClasses, setProvisioningClasses] = useState(false);

  const fetchData = useCallback(async () => {
    const { data: schoolData } = await supabase.from("schools").select("*").order("name");
    if (schoolData) {
      setSchools(schoolData as School[]);
      if (schoolData.length > 0 && !selectedSchoolId) setSelectedSchoolId((schoolData[0] as School).id);
    }

    let classQuery = supabase.from("classes").select("*").order("name");
    if (selectedSchoolId) classQuery = classQuery.eq("school_id", selectedSchoolId);
    const { data: classData } = await classQuery;
    if (classData) setClasses(classData as SchoolClass[]);

    let studentQuery = supabase.from("students").select("*").order("created_at", { ascending: false });
    if (selectedSchoolId) studentQuery = studentQuery.eq("school_id", selectedSchoolId);
    const { data: studentData } = await studentQuery;
    if (studentData) setStudents(studentData as Student[]);
  }, [selectedSchoolId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const channel = supabase
      .channel("students-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "students" }, () => fetchData())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchData]);

  // Ensures ECD + Grade 1A-8D exist for the selected school + shift +
  // academic year. Fires as soon as those three values are all known and
  // Level is Primary — not gated behind the user manually touching the
  // Level field first, so it's reliable even if Primary is selected
  // before the school finishes loading.
  const ensureStandardPrimaryClasses = useCallback(async (schoolId: string, forShift: "morning" | "afternoon", year: number) => {
    if (!schoolId) return;
    setProvisioningClasses(true);
    try {
      const { data: existing } = await supabase
        .from("classes")
        .select("name")
        .eq("school_id", schoolId)
        .eq("level", "primary")
        .eq("shift", forShift)
        .eq("academic_year", year);

      const existingNames = new Set((existing || []).map((c: any) => c.name));
      const missing = STANDARD_PRIMARY_CLASSES.filter((c) => !existingNames.has(c.name));

      if (missing.length > 0) {
        const rows = missing.map((c) => ({
          name: c.name,
          level: "primary" as const,
          shift: forShift,
          school_id: schoolId,
          grade: c.grade,
          section: c.section,
          academic_year: year,
        }));
        const { error: insertError } = await supabase.from("classes").insert(rows);
        // Ignore duplicate-key races: another device may have provisioned
        // the same standard classes for this school/shift/year a moment ago.
        if (insertError && insertError.code !== "23505") {
          console.error("Failed to provision standard classes:", insertError);
        }
      }
    } finally {
      setProvisioningClasses(false);
      await fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (level === "primary" && selectedSchoolId) {
      ensureStandardPrimaryClasses(selectedSchoolId, shift, academicYear);
    }
  }, [level, selectedSchoolId, shift, academicYear, ensureStandardPrimaryClasses]);

  const filteredClasses = classes
    .filter((c) => c.level === level && c.shift === shift && (c as any).academic_year === academicYear)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const generateRefCode = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(1000 + Math.random() * 9000);
    return `NUS-${year}-${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const code = generateRefCode();

    const { data, error: insertError } = await supabase
      .from("students")
      .insert({
        full_name: fullName,
        date_of_birth: dob || null,
        gender,
        level,
        guardian_name: guardianName,
        guardian_phone: guardianPhone,
        guardian_email: guardianEmail || null,
        guardian_id: user?.id,
        reference_code: code,
        class_id: classId || null,
        shift,
        status: "approved",
        school_id: selectedSchoolId || null,
        academic_year: academicYear,
        enrollment_status: "active",
      })
      .select()
      .single();

    await logActivity("registered student", "student", data?.id, fullName, selectedSchoolId, `Ref: ${code}`);

    setLoading(false);
    if (insertError) {
      setError(t("err_reg_save"));
      return;
    }

    setRefCode(code);
    setShowSuccess(true);
    setFullName(""); setDob(""); setGuardianName(""); setGuardianPhone(""); setGuardianEmail(""); setClassId("");
    await fetchData();
  };

  return (
    <div className="animate-fadeIn">
      <ExcelBulkUpload
        type="student"
        schoolId={selectedSchoolId}
        onComplete={fetchData}
      />

      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <UserPlus className="w-6 h-6" /> {t("reg_title")}
          </h2>
        </div>
        <p className="text-gray-500 text-sm mb-5">{t("reg_desc")}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="form-group">
            <label className="form-label">{t("lbl_select_school")}</label>
            <div className="relative">
              <SchoolIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <select className="form-input pl-10" value={selectedSchoolId} onChange={(e) => setSelectedSchoolId(e.target.value)} required>
                <option value="">{t("ph_school_select")}</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {schools.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No schools yet — add one from the Dashboard's "Schools" tab first.
              </p>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Academic Year</label>
            <select className="form-input" value={academicYear} onChange={(e) => { setAcademicYear(Number(e.target.value)); setClassId(""); }}>
              {ACADEMIC_YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_name")}</label>
              <input className="form-input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder={t("ph_reg_name")} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_dob")}</label>
              <input className="form-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_gender")}</label>
              <select className="form-input" value={gender} onChange={(e) => setGender(e.target.value as any)} required>
                <option value="female">{t("opt_female")}</option>
                <option value="male">{t("opt_male")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_level")}</label>
              <select className="form-input" value={level} onChange={(e) => { setLevel(e.target.value as any); setClassId(""); }} required>
                <option value="preschool">{t("opt_level_preschool")}</option>
                <option value="primary">{t("opt_level_primary")}</option>
                <option value="secondary">{t("opt_level_secondary")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_shift")}</label>
              <select className="form-input" value={shift} onChange={(e) => { setShift(e.target.value as any); setClassId(""); }} required>
                <option value="morning">{t("opt_shift_morning")}</option>
                <option value="afternoon">{t("opt_shift_afternoon")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                {t("lbl_reg_class")}
                {level === "primary" && provisioningClasses && (
                  <span className="text-xs text-gray-400 ml-2">(setting up ECD, Grade 1A–8D...)</span>
                )}
              </label>
              <select className="form-input" value={classId} onChange={(e) => setClassId(e.target.value)}>
                <option value="">{t("opt_no_class")}</option>
                {filteredClasses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {level === "primary" && filteredClasses.length === 0 && !provisioningClasses && selectedSchoolId && (
                <p className="text-xs text-gray-400 mt-1">
                  Standard classes (ECD, Grade 1A–8D) will appear here automatically.
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_guardian")}</label>
              <input className="form-input" type="text" value={guardianName} onChange={(e) => setGuardianName(e.target.value)} required placeholder={t("ph_reg_guardian")} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_phone")}</label>
              <input className="form-input" type="text" value={guardianPhone} onChange={(e) => setGuardianPhone(e.target.value)} required placeholder={t("ph_reg_phone")} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_reg_email")}</label>
              <input className="form-input" type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)} placeholder={t("ph_reg_email")} />
            </div>
          </div>

          {error && <div className="text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm mb-4">{error}</div>}

          <button type="submit" disabled={loading} className="btn-cyan">
            <UserPlus className="w-5 h-5" /> {loading ? t("btn_saving") : t("btn_submit_reg")}
          </button>
        </form>

        {showSuccess && (
          <div className="mt-5 p-5 rounded-lg bg-[#F8FAFC] border-l-5 border-l-[#38B6FF] border border-[#E2E8F0]">
            <h3 className="text-[#1C3988] mb-2 flex items-center gap-2 font-bold">
              <CheckCircle2 className="w-5 h-5" /> {t("reg_success_title")}
            </h3>
            <p><strong>{t("reg_ref_code")}</strong>: <span className="text-[#7A2222] font-bold">{refCode}</span></p>
            <p className="mt-2 text-sm text-gray-600">{t("reg_success_msg")}</p>
          </div>
        )}
      </div>

      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <Users className="w-6 h-6" /> {t("reg_list_title")} ({students.length})
          </h2>
        </div>
        {students.length === 0 ? (
          <p className="text-gray-400 italic py-4">{t("reg_list_empty")}</p>
        ) : (
          <div className="space-y-2">
            {students.map((s) => (
              <div key={s.id} className="attendance-row">
                <div>
                  <strong>{s.full_name}</strong>
                  <span className="text-sm text-gray-400 ml-2">
                    | {s.gender} | {s.level} | {s.shift} | {s.guardian_name} | {s.guardian_phone}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs bg-[#1C3988] text-white px-2 py-1 rounded-full font-semibold">{s.reference_code}</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${s.status === "approved" ? "bg-[#2E7D32] text-white" : "bg-amber-500 text-white"}`}>
                    {s.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
